import Image from 'next/image'
import { sanitizeHTML } from "@/lib/utils";
import { getReadableImageUrl } from "@/lib/directus";

interface ProcedureInPlanoData {
  heading?: {
    text?: string;
    title?: string;
  };
  text?: {
    text?: string;
  };
  content_collection?: {
    id: string;
    site_content_collections_id: {
      collection: {
        item: {
          title: string;
          text: string;
        };
      }[];
    };
  }[];
}

export const fields = [
  // headings
  "heading.text",
  "heading.title",

  // text
  "text.text",

  // content collection / text for timeline steps
  "content_collection.id",
  "content_collection.site_content_collections_id.collection.item:site_content.title",
  "content_collection.site_content_collections_id.collection.item:site_content.text",

] as const;

interface ProcedureInPlanoProps {
  data?: ProcedureInPlanoData;
}

export default function ProcedureInPlano({ data }: ProcedureInPlanoProps) {
  return (
    <section className="w-full bg-light relative overflow-hidden">
      <div className="relative w-full lg:container mx-auto px-8 lg:px-20 py-8 xl:py-16 xl:flex xl:justify-between">

        {/* Header Section */}
        <div className="w-full pb-8 xl:w-[35%]">
          <h2 className="text-[2rem] font-essonnes font-light leading-tight md:text-5xl lg:text-[3.375rem] text-primary mb-10">
            {data?.heading?.title}
          </h2>
          <div 
            className="text-[1rem] font-normal leading-normal md:text-xl font-greycliff text-primary md:leading-[1.83] tracking-tight max-w-[38.5rem]"
            dangerouslySetInnerHTML={sanitizeHTML(data?.text?.text || "")}
          />
        </div>

        {/* Timeline Section */}
        <div className="relative mt-8 w-full xl:w-[60%]">

          {/* Timeline Line */}
          <div className="absolute left-[1.2rem] lg:left-[49%] top-0 bottom-0 w-px bg-primary transform -translate-x-1/2"></div>

          {/* Timeline Steps */}
          <div className="relative lg:h-auto pr-[1.25rem]">
            
            {data?.content_collection?.map((collection, index) => {
              const items = collection.site_content_collections_id.collection;

              const titleItem = collection?.site_content_collections_id?.collection.find(
                item => item.item.title
              )?.item;
              
              const textItem = collection?.site_content_collections_id?.collection.find(
                item => item.item.text
              )?.item;

              const stepNumber = index + 1;
              const isEven = index % 2 !== 0;

              return (
                <div key={collection.id} className={`flex pb-16 lg:pb-0 items-start gap-6 lg:gap-11 ${isEven ? 'lg:flex-row-reverse' : ''}`}>
                  <div className="w-[28.75rem] hidden lg:block"></div>
                  <div className="relative">
                    <div className="w-[2.4rem] h-[2.4rem] rounded-full bg-primary flex items-center justify-center desktopFadeSimple">
                      <span className="text-xl font-semibold font-greycliff text-light tracking-tight">{stepNumber}</span>
                    </div>
                  </div>
                  <div className={`w-auto lg:w-[28.75rem] ${isEven ? 'text-left lg:text-right desktopFadeFromLeft' : 'desktopFadeFromRight'}`}>
                    <h3 className="text-[1.5rem] lg:text-[2rem] font-medium font-greycliff text-primary leading-[1.36] tracking-tight mb-6">
                      {titleItem?.title}
                    </h3>
                    <div 
                      className="text-[1rem] font-normal leading-normal md:text-lg md:leading-relaxed font-greycliff text-primary tracking-tight [&>*]:text-[1rem] [&>*]:font-normal [&>*]:leading-normal md:[&>*]:text-lg md:[&>*]:leading-relaxed [&>ul]:py-4 [&>ul]:list-disc [&>ul]:pl-5 [&_a]:underline [&_a:hover]:no-underline"
                      dangerouslySetInnerHTML={sanitizeHTML(textItem?.text || "")}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  )
} 